// src/components/agenda/TaskForm.js
import React, { useState } from 'react';
import { generateId } from '../../services/utils';
import AssigneeAutocomplete from '../AssigneeAutocomplete'; 
import ProjectAutocomplete from '../ProjectAutocomplete'; 
import { getNextTaskNumber } from '../../services/firebase'; 

const TaskForm = ({ 
  task = null, 
  onSave, 
  onCancel, 
  companyUsers = [], 
  teams = [], 
  setNotification, 
  agendaItemId, 
  meetingId, 
  companyId, 
  currentUserId 
}) => {
  const [title, setTitle] = useState(task ? task.title : '');
  const [description, setDescription] = useState(task ? task.description : '');
  // Logik an TaskDashboard angepasst: `assignees` ist ein Array.
  const [assignees, setAssignees] = useState(task ? task.assignees || [] : []);
  const [dueDate, setDueDate] = useState(task ? task.dueDate : '');
  const [status, setStatus] = useState(task ? task.status : 'Offen');
  
  // Für Projekte
  const [projectId, setProjectId] = useState(task?.projectId || '');
  const [projectName, setProjectName] = useState(task?.project || '');

  // Ladezustand für Nummer
  const [isFetchingNumber, setIsFetchingNumber] = useState(false);

  // Eindeutige ID für Formular-Elemente, basierend auf Agenda-Item und Task
  const formIdSuffix = `${agendaItemId}-${task ? task.id : 'new'}`;

  const handleSaveClick = async () => {
    if (!title.trim()) {
      if (setNotification) {
          setNotification({ message: 'Bitte geben Sie einen Titel für die Aufgabe ein.', type: 'error' });
      } else {
          alert('Bitte geben Sie einen Titel für die Aufgabe ein.');
      }
      return;
    }

    // Sicherheits-Check, falls meetingId nicht übergeben wurde
    if (!meetingId) {
        console.error("TaskForm: meetingId is missing, cannot save task.");
        if (setNotification) {
            setNotification({ message: 'Fehler: Die Protokoll-ID fehlt. Aufgabe kann nicht gespeichert werden.', type: 'error' });
        }
        return;
    }

    // Sequentielle Nummer für NEUE Tasks holen
    let taskNumber = task ? task.sequentialNumber : null;
    if (!task && companyId) { // Nur für NEUE Tasks und wenn companyId vorhanden ist
        setIsFetchingNumber(true);
        try {
            const { data } = await getNextTaskNumber({ companyId });
            taskNumber = data.number;
        } catch (error) {
            console.error("Fehler beim Abrufen der Task-Nummer:", error);
            setNotification({ message: 'Fehler: Konnte keine Task-Nummer abrufen.', type: 'error' });
            setIsFetchingNumber(false);
            return; // Abbruch
        }
        setIsFetchingNumber(false);
    }

    // Wir leiten den 'isCompleted'-Boolean-Wert direkt vom 'status'-Text ab.
    const isTaskCompleted = (status === 'Erledigt');

    onSave({
      // *** WICHTIG: Bestehende Daten übernehmen, damit IDs/Creator nicht verloren gehen ***
      ...(task || {}),

      // Formular-Werte (überschreiben ggf. alte Werte)
      id: task ? task.id : generateId(),
      title,
      description,
      assignees, // Wird als Array übergeben
      dueDate,
      status,
      project: projectName, // Speichere Namen für Anzeige
      projectId: projectId, // Speichere ID für Verknüpfung
      isCompleted: isTaskCompleted, 
      meetingId: meetingId, 
      sequentialNumber: taskNumber, 
    });
  };
  
  const handleAssigneeSelect = (selection) => {
      // Stellt sicher, dass assignees immer ein Array ist, auch bei Einzelauswahl
      setAssignees(selection ? [selection] : []);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-4 rounded-2xl shadow-lg mb-5 border border-gray-200 dark:border-gray-700">
      <h4 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{task ? 'Aufgabe bearbeiten' : 'Neue Aufgabe hinzufügen'}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="Titel der Aufgabe"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors duration-200"
          required
          id={`task-title-${formIdSuffix}`}
          name={`task-title-${formIdSuffix}`}
        />
        <AssigneeAutocomplete
          users={companyUsers}
          teams={teams}
          // Nimmt den Namen des ersten zugewiesenen Objekts
          initialValue={assignees[0]?.name}
          onSelect={handleAssigneeSelect}
          inputId={`task-assignee-${formIdSuffix}`}
        />
        <textarea
          placeholder="Aufgabenbeschreibung (Markdown unterstützt)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors duration-200 resize-y min-h-[60px] md:col-span-2"
          rows="2"
          id={`task-desc-${formIdSuffix}`}
          name={`task-desc-${formIdSuffix}`}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors duration-200"
          id={`task-status-${formIdSuffix}`}
          name={`task-status-${formIdSuffix}`}
        >
          <option value="Offen">Offen</option>
          <option value="In Bearbeitung">In Bearbeitung</option>
          <option value="Erledigt">Erledigt</option>
        </select>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors duration-200"
          id={`task-duedate-${formIdSuffix}`}
          name={`task-duedate-${formIdSuffix}`}
        />
        
        {/* Projekt Auswahl */}
        <div className="w-full">
            <ProjectAutocomplete 
                companyId={companyId} 
                userUid={currentUserId} 
                initialValue={projectId || ''} 
                onSelect={(p) => {
                    setProjectId(p.id);
                    setProjectName(p.name);
                }}
                onNameResolved={(name) => setProjectName(name)}
                label="" // Kein Label hier, da Placeholder im Input
            />
        </div>

        <div className="col-span-1 md:col-span-2 flex justify-end space-x-3 mt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-all duration-200 ease-in-out transform hover:scale-105 font-medium shadow-sm"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={isFetchingNumber} // Button während des Ladens sperren
            className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 ease-in-out transform hover:scale-105 font-medium shadow-md ${isFetchingNumber ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isFetchingNumber ? 'Lade Nr...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskForm;